import { HiChevronDown } from '@react-icons/all-files/hi/HiChevronDown';
import { useMemo } from 'react';

import { assignLocation } from '~/shared/helpers/browser-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import { LOCALES, type Locale } from '~/universal/constants';

import './language-switcher.scss';

type UseLanguageSwitcherProps = Readonly<{
  lng: Locale;
}>;

const LOCALE_PATH_RE = /^\/(ua|ru|en)(?=\/|$)/;

const getPathWithLocale = (locale: Locale): string => {
  if (typeof window === 'undefined') {
    return `/${locale}`;
  }

  const pathname = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;

  const nextPathname = LOCALE_PATH_RE.test(pathname)
    ? pathname.replace(LOCALE_PATH_RE, `/${locale}`)
    : `/${locale}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;

  return `${nextPathname}${search}${hash}`;
};

const useLanguageSwitcher = ({ lng }: UseLanguageSwitcherProps) => {
  const t = useTypedTranslation(['page', 'text']);

  const switchLanguage = (locale: Locale) => {
    const nextHref = getPathWithLocale(locale);

    localStorage.setItem('locale', locale);

    if (locale === lng && typeof window !== 'undefined') {
      return;
    }

    assignLocation(nextHref);
  };

  const menuItems = useMemo(
    () => [
      {
        key: `locale-${lng}`,
        className: 'language-switcher__item',
        label: (
          <>
            <span>{lng?.toUpperCase()}</span>
            <HiChevronDown className="language-switcher__menu-item-chevron" />
          </>
        ),
        children: [
          {
            key: LOCALES.UA,
            className: 'language-switcher__item',
            label: <span onClick={() => switchLanguage(LOCALES.UA)}>{t('text.ua')?.toUpperCase()}</span>,
          },
          {
            key: LOCALES.RU,
            className: 'language-switcher__item',
            label: <span onClick={() => switchLanguage(LOCALES.RU)}>{t('text.ru')?.toUpperCase()}</span>,
          },
          {
            key: LOCALES.EN,
            className: 'language-switcher__item',
            label: <span onClick={() => switchLanguage(LOCALES.EN)}>{t('text.en')?.toUpperCase()}</span>,
          },
        ],
      },
    ],
    [lng, t],
  );

  return {
    menuItems,
  };
};

export default useLanguageSwitcher;
